import { getHostBridge, type MediaFile } from "@emkit/adapter-emdash/bridge";
import { useEffect, useRef, useState } from "react";

interface Props {
  open: boolean;
  /** MIME-type filter prefix passed to the host (e.g. "image/"). */
  accept?: string;
  /** User picks a file from the grid → onSelect(url). */
  onSelect: (url: string) => void;
  onClose: () => void;
}

/**
 * Modal media picker. Two flows in one UI:
 *  - Browse: lists files the host already has (via emkit:media:list).
 *    Hosts that don't implement the protocol return empty after a 2s
 *    grace; the empty state nudges the user to upload.
 *  - Upload: file input + drop zone, uses the existing emkit:media:upload
 *    bridge. Newly uploaded files are prepended to the grid so users see
 *    immediate feedback.
 *
 * Standalone editor (not embedded) hides the upload + browse buttons —
 * the host is the storage. Standalone users keep pasting URLs directly
 * via the parent MediaField input.
 */
export function MediaGalleryPicker({ open, accept = "image/*", onSelect, onClose }: Props) {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Refresh the list whenever the modal opens. Closing wipes state so the
  // next open starts clean — keeps memory bounded if the gallery has lots
  // of items the user never reuses.
  useEffect(() => {
    if (!open) {
      setFiles([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const mimePrefix = accept.endsWith("/*") ? accept.slice(0, -1) : undefined;
    getHostBridge()
      .listMedia({ mimePrefix, limit: 50 })
      .then((res) => {
        if (cancelled) return;
        setFiles(res.files);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load media");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, accept]);

  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const uploaded = await getHostBridge().uploadMedia(
        file.name,
        file.type || "application/octet-stream",
        bytes,
      );
      setFiles((prev) => [uploaded, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) await upload(file);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await upload(file);
  };

  if (!open) return null;

  return (
    <div
      className="emke-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Media gallery"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="emke-modal" style={{ maxWidth: 720, maxHeight: "82vh" }}>
        <header className="emke-modal-header">
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-dim)",
              }}
            >
              Media
            </div>
            <h2 style={{ margin: "4px 0 0 0", fontSize: 18 }}>Pick or upload an image</h2>
          </div>
          <button
            type="button"
            className="emke-btn"
            onClick={onClose}
            aria-label="Close media gallery"
          >
            ✕
          </button>
        </header>

        <div
          className="emke-modal-body"
          style={{ padding: 16, overflow: "auto" }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: 10,
              border: `1.5px dashed ${dragOver ? "var(--accent)" : "var(--border)"}`,
              borderRadius: 8,
              background: dragOver ? "var(--panel-soft)" : "transparent",
              transition: "background 0.12s, border-color 0.12s",
              marginBottom: 14,
            }}
          >
            <span style={{ fontSize: 12, color: "var(--fg-muted)", flex: 1 }}>
              Drag a file here, or
            </span>
            <button
              type="button"
              className="emke-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? "Uploading…" : "Choose file"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              onChange={handleFileInput}
              style={{ display: "none" }}
            />
          </div>

          {error && (
            <div
              style={{
                fontSize: 12,
                color: "var(--danger, #b91c1c)",
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div
              style={{
                padding: 32,
                textAlign: "center",
                color: "var(--fg-dim)",
                fontSize: 13,
              }}
            >
              Loading…
            </div>
          ) : files.length === 0 ? (
            <div
              style={{
                padding: 32,
                textAlign: "center",
                color: "var(--fg-dim)",
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              No media yet.
              <br />
              Drop a file above or click <strong>Choose file</strong> to upload your first.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                gap: 10,
              }}
            >
              {files.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className="emke-media-tile"
                  onClick={() => {
                    onSelect(f.url);
                    onClose();
                  }}
                  title={`${f.filename} · ${formatSize(f.size)}`}
                >
                  {f.mimeType.startsWith("image/") ? (
                    <img src={f.url} alt={f.filename} loading="lazy" />
                  ) : (
                    <div className="emke-media-tile-fallback">
                      <span style={{ fontSize: 22 }}>📄</span>
                      <span style={{ fontSize: 11 }}>{f.mimeType}</span>
                    </div>
                  )}
                  <span className="emke-media-tile-name">{f.filename}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
